import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const QuestionRow = ({ question, questionNumber, isExample, answer, handleInputChange, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const rawText = question.question_text.replace(/\(Example\)/gi, '').trim();
  const parts = rawText.split('{{big_gap}}');
  const beforeGap = parts[0];
  const afterGap = parts.length > 1 ? parts[1] : null;

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

  return (
    <div className={`p-4 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700 hover:border-blue-200 shadow-sm'}`}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 font-black text-slate-400 bg-slate-50 dark:bg-slate-900 px-2.5 py-1 rounded-xl text-sm border border-slate-100 dark:border-slate-800 shadow-sm mt-0.5">
          {isExample ? 'e.g.' : `${questionNumber}.`}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-700 dark:text-slate-300 text-base leading-relaxed m-0">
            {beforeGap}
          </p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            {showIncorrectAnswerInReview ? (
              <div className="exercise-input-text inline-flex items-center bg-red-50 px-3 py-1.5 rounded-xl border border-red-100">
                {question.user_answer?.answer
                  ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer.answer}</span>
                  : <span className="text-gray-400 mr-2 font-bold">__</span>
                }
                <span className="text-green-600 font-black">{question.accepted_answers[0]}</span>
              </div>
            ) : (
              <input
                type="text"
                value={isExample ? (question.accepted_answers && question.accepted_answers[0]) || '' : (showHint || (isReviewMode && isAnswerCorrect)) ? question.accepted_answers[0] : answer || ''}
                onChange={isExample ? undefined : (e) => handleInputChange(question.id, e.target.value)}
                readOnly={isExample || showHint || !isHydrated}
                disabled={isReviewMode || !isHydrated}
                className={`exercise-input-field exercise-input-text w-48 text-center py-2 font-black text-lg !rounded-xl ${isExample ? 'exercise-example-text' : ''} ${showHint && !isExample ? 'hint-input' : ''}`}
                autoComplete="off"
              />
            )}
            {afterGap && <span className="font-bold text-slate-600 dark:text-slate-400">{afterGap}</span>}
            {isReviewMode && (
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 ${isAnswerCorrect ? 'bg-hupath-leaf' : 'bg-red-500'}`}>
                <span className="material-symbols-outlined text-sm font-bold">{isAnswerCorrect ? 'check' : 'close'}</span>
              </div>
            )}
            {isReviewMode && !isExample && <QuestionLearnButton question={question} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const MoversReadingPart1 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { shared_options } = exerciseData.content_data;
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const exampleQuestion = exerciseData.questions.find(q => q.order_index === 0);
  const mainQuestions = exerciseData.questions.filter(q => q.order_index !== 0).sort((a, b) => a.order_index - b.order_index);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-3">
        {exampleQuestion && (
          <QuestionRow
            question={exampleQuestion}
            isExample={true}
            answer={answers[exampleQuestion.id]}
            handleInputChange={handleInputChange}
            isReviewMode={isReviewMode}
            isCorrect={exampleQuestion.is_correct}
            isHydrated={isHydrated}
          />
        )}
        {mainQuestions.map((q, index) => (
          <QuestionRow
            key={q.id}
            question={q}
            questionNumber={index + 1}
            isExample={false}
            answer={answers[q.id]}
            handleInputChange={handleInputChange}
            showHint={showHint}
            isReviewMode={isReviewMode}
            isCorrect={q.is_correct}
            isHydrated={isHydrated}
          />
        ))}

        {/* Mobile: show shared options below questions */}
        {shared_options && shared_options.length > 0 && (
          <div className="md:hidden mt-4 bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-4 shadow-sm">
            <div className="grid grid-cols-2 gap-3">
              {shared_options.map(option => (
                <div key={option.id} className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border-2 border-gray-200 dark:border-slate-700">
                  <img src={`${ASSET_BASE_URL}${option.image_url}`} alt={option.image_text || option.id} className="w-full h-auto object-contain rounded-xl" />
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300 text-center">{option.image_text || option.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoversReadingPart1;
